import type { FC, ReactElement, ReactNode } from 'react';

import { Icon } from '@iconify/react';
import { IconButton, List, SwipeableDrawer } from '@mui/material';
import { useEffect, useState } from 'react';
import { tss } from 'tss-react/mui';

import type { NavItem } from '../../../types';

import { NavMenuItem } from '../../NavMenuItem';

const HEADER_OFFSET = 40;

const useStyles = tss.create(({ theme }) => ({
  buttonArray: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
    height: '100%',
    marginRight: 8,
  },
  drawer: {
    background: theme.palette.background.switch,
    boxShadow: '0px 3px 6px #00000059',
    flexShrink: 0,
    width: 'auto',
    zIndex: theme.zIndex.appBar + 1,
  },
  drawerOpen: {
    '& [class*="MuiTypography-body1"]': {
      fontSize: theme.typography.h6.fontSize,
    },
    'background': theme.palette.common.white,
    'transition': theme.transitions.create('all', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.sharp,
    }),
    'width': 240,
  },
  menuIconButton: {
    '&:hover': {
      background: 'transparent',
    },
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  navBottom: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  secondHeader: {
    alignItems: 'center',
    background: theme.palette.grey[200],
    boxShadow: '0px 3px 6px #00000026',
    display: 'flex',
    height: HEADER_OFFSET,
    justifyContent: 'space-between',
    position: 'sticky',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    top: 0,
    zIndex: theme.zIndex.appBar + 3,
  },
  secondHeaderIcon: {
    color: theme.palette.common.black,
    height: 38,
    justifyContent: 'start',
    marginLeft: 16,
    width: 38,
  },
}));

type Props = {
  activeNavItem?: NavItem;
  headerAdditions: ReactNode;
  navItems?: readonly NavItem[];
  version: ReactElement;
};

export const MobileNavigation: FC<Props> = ({
  activeNavItem,
  headerAdditions,
  navItems,
  version,
}) => {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState<NavItem>();

  const onClose = () => setOpen(false);

  useEffect(() => {
    setExpandedItem(activeNavItem);
  }, [activeNavItem]);

  const toggleOpen = () => setOpen(prevState => !prevState);
  return (
    <>
      <header aria-label="Mobile Header" className={classes.secondHeader}>
        <IconButton
          aria-label={open ? 'Close Menu' : 'Open Menu'}
          className={classes.menuIconButton}
          onClick={toggleOpen}
        >
          <Icon
            className={classes.secondHeaderIcon}
            icon={open ? 'material-symbols:close' : 'material-symbols:menu'}
          />
        </IconButton>
        <div className={classes.buttonArray}>{headerAdditions}</div>
      </header>
      <SwipeableDrawer
        anchor="left"
        classes={{
          paper: classes.drawerOpen,
          paperAnchorLeft: classes.drawer,
        }}
        ModalProps={{ keepMounted: true }}
        onClose={onClose}
        onOpen={() => setOpen(true)}
        open={open}
        variant="temporary"
      >
        <nav aria-label="Main Navigation" className={classes.nav}>
          <List>
            {(navItems ?? []).map(item => (
              <NavMenuItem
                active={item === activeNavItem}
                item={item}
                key={item.target}
                onClick={() => {
                  setExpandedItem(item === expandedItem ? undefined : item);
                  onClose();
                }}
              />
            ))}
          </List>

          <div className={classes.navBottom}>{version}</div>
        </nav>
      </SwipeableDrawer>
    </>
  );
};
